import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Loader2, Search, Sparkles, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import { api, domaineLabel, errMsg } from "@/lib/api";
import SavoirFiches from "@/components/SavoirFiches";
import SourcesNotices from "@/components/SourcesNotices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EXEMPLES = ["Ma clim ne fait plus de froid", "Une caméra est hors ligne", "Plus de Wi-Fi dans le bureau", "Mon différentiel saute"];
const CODES_EXEMPLES = ["U4", "E7", "A5", "RISO", "ADOPTION FAILED"];

export const RecherchePanneIA = () => {
  const [panne, setPanne] = useState("");
  const [code, setCode] = useState("");
  const [ia, setIa] = useState(null);
  const [fichesCode, setFichesCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyCode, setBusyCode] = useState(false);

  const chercher = async (texte) => {
    const message = (texte ?? panne).trim();
    if (message.length < 5) return toast.error("Décrivez votre panne en quelques mots");
    setPanne(message);
    setBusy(true);
    setIa(null);
    try {
      const { data } = await api.post("/tutorials/search-ia", { message });
      setIa(data);
    } catch (e) {
      toast.error(errMsg(e, "Recherche IA indisponible"));
    } finally {
      setBusy(false);
    }
  };

  const chercherCode = async (valeur) => {
    const cible = (valeur ?? code).trim();
    if (!cible) return toast.error("Saisissez le code affiché par l'appareil");
    setCode(cible);
    setBusyCode(true);
    setFichesCode(null);
    try {
      const { data } = await api.get("/savoir/code", { params: { code: cible } });
      setFichesCode(data.fiches || []);
      if (!data.fiches?.length) toast.info("Code inconnu de notre base : décrivez la panne juste en dessous");
    } catch (e) {
      toast.error(errMsg(e, "Recherche par code indisponible"));
    } finally {
      setBusyCode(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-900 bg-slate-900 text-white p-5 sm:p-6 space-y-4" data-testid="ticket-ia-panel">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50">
          <WandSparkles size={14} className="text-[#005CFF]" /> Recherche de panne
        </div>
        <h2 className="font-display text-lg md:text-lg font-bold tracking-tight mt-2">Décrivez votre panne avant de créer le ticket</h2>
        <p className="text-sm text-white/60 mt-1">
          L'assistant s'appuie sur les notices constructeur et notre savoir terrain : il identifie le domaine, propose le
          geste simple à tenter, et vous emmène au bon formulaire.
        </p>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3" data-testid="ticket-code-panel">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">Un code d'erreur s'affiche sur l'appareil ?</div>
        <div className="flex gap-2">
          <Input
            data-testid="ticket-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && chercherCode()}
            placeholder="Ex : U4, E7, RISO, adoption failed"
            className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#005CFF]"
          />
          <Button data-testid="ticket-code-btn" onClick={() => chercherCode()} disabled={busyCode} className="h-11 shrink-0 bg-white/10 hover:bg-white/20">
            {busyCode ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {CODES_EXEMPLES.map((c, i) => (
            <button
              key={c}
              data-testid={`ticket-code-exemple-${i}`}
              onClick={() => chercherCode(c)}
              className="text-xs font-mono rounded-full border border-white/15 px-2.5 py-1 text-white/70 hover:border-[#005CFF] hover:text-white transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
        {fichesCode?.length > 0 && (
          <div data-testid="ticket-code-result">
            <SavoirFiches fiches={fichesCode} sombre />
          </div>
        )}
        {fichesCode?.length === 0 && (
          <p className="text-sm text-white/50" data-testid="ticket-code-vide">
            Ce code n'est pas encore dans notre base. Décrivez la panne ci-dessous, l'assistant prend le relais.
          </p>
        )}
      </div>

      <Textarea
        data-testid="ticket-ia-input"
        value={panne}
        onChange={(e) => setPanne(e.target.value)}
        rows={3}
        placeholder="Ex : depuis l'orage, mon écran Loxone reste noir et le portail ne s'ouvre plus"
        className="bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#005CFF]"
      />

      <div className="flex flex-wrap gap-2">
        {EXEMPLES.map((ex, i) => (
          <button
            key={ex}
            data-testid={`ticket-ia-exemple-${i}`}
            onClick={() => chercher(ex)}
            className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-white/70 hover:border-[#005CFF] hover:text-white transition-colors"
          >
            {ex}
          </button>
        ))}
      </div>

      <Button data-testid="ticket-ia-btn" onClick={() => chercher()} disabled={busy} className="bg-[#005CFF] hover:bg-[#0047CC]">
        {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Sparkles size={15} className="mr-2" />}
        {busy ? "Analyse en cours…" : "Analyser ma panne"}
      </Button>

      {ia && (
        <div className="space-y-3 pt-2 border-t border-white/10" data-testid="ticket-ia-result">
          {ia.reponse && <p className="text-sm text-white/85 leading-relaxed" data-testid="ticket-ia-reponse">{ia.reponse}</p>}
          {ia.domaine_probable && (
            <div className="text-xs text-white/50">
              Domaine identifié : <span className="text-white font-semibold">{domaineLabel(ia.domaine_probable)}</span>
            </div>
          )}
          <SavoirFiches fiches={ia.fiches} sombre />
          {ia.tutos?.length > 0 && (            <div className="space-y-1.5" data-testid="ticket-ia-tutos">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">À tenter avant le ticket</div>
              {ia.tutos.slice(0, 3).map((t) => (
                <Link
                  key={t.id}
                  to="/tutos"
                  data-testid={`ticket-ia-tuto-${t.id}`}
                  className="flex items-start gap-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <BookOpen size={14} className="mt-0.5 shrink-0 text-[#4D93FF]" /> {t.titre}
                </Link>
              ))}
            </div>
          )}
          <SourcesNotices sources={ia.sources} sombre />
          {ia.domaine_probable && (
            <Link
              to={`/declarer/${ia.domaine_probable}`}
              data-testid="ticket-ia-declarer-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#4D93FF] hover:text-white transition-colors"
            >
              Créer le ticket {domaineLabel(ia.domaine_probable)} <ArrowRight size={15} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default RecherchePanneIA;
